import { captureError, setUser } from '@smg-real-estate/browser-rum';
import { defineStore } from 'pinia';

import { fetchUserDetails } from '@/api/user';
import { isExpirationDateValid, twoDaysInTheFuture } from '@/helpers/Mfa/mfaModalHelper';
import { SourcePlatform, UserGroup, UserTypeLogin, type AuthorizedUser } from '@/types';

export const InitialState = 'INITIAL_STATE';

type UserStoreState = AuthorizedUser & { sourcePlatform: SourcePlatform };

function getInitialState(): UserStoreState {
  return {
    userId: InitialState,
    auth0Id: '',
    email: '',
    logonId: '',
    groups: [],
    picture: undefined,
    name: undefined,
    userType: undefined,
    sourcePlatform: SourcePlatform.SMG,
    userDetails: undefined,
    mfaSkipped: false,
  };
}

export const useUserStore = defineStore('user', {
  state: (): UserStoreState => getInitialState(),
  getters: {
    getUserTypeLogin: (state): UserTypeLogin =>
      state.groups.includes(UserGroup.CUSTOMER_SERVICE_AGENT)
        ? UserTypeLogin.SMG_INTERNAL
        : UserTypeLogin.AGENCY,
    hasCCCGroup: (state): boolean => state.groups.includes(UserGroup.CUSTOMER_SERVICE_AGENT),
    hasUserGroup:
      (state) =>
      (group: UserGroup): boolean => {
        return state.groups.includes(group);
      },
    hasMfa: (state): boolean => state.userDetails?.requiredMFA === true,
    showMfaToggle: (state): boolean => state.userDetails?.exemptFromMFA === true,
  },
  actions: {
    setUser(user: AuthorizedUser): void {
      this.userId = user.userId;
      this.auth0Id = user.auth0Id;
      this.email = user.email;
      this.logonId = user.logonId;
      this.picture = user?.picture;
      this.name = user?.name;
      this.groups = user.groups;
    },
    setMfaSkipped(mfaSkipped: boolean): void {
      const localStorageItem = localStorage.getItem(`mfaSkipped_${this.userId}`);
      let sessionObject = JSON.parse(localStorageItem || '{}');

      this.mfaSkipped = mfaSkipped;

      if (this.mfaSkipped) {
        if (!isExpirationDateValid(sessionObject?.expiresAt)) {
          sessionObject = {
            expiresAt: twoDaysInTheFuture(),
          };
        }
        localStorage.setItem(`mfaSkipped_${this.userId}`, JSON.stringify(sessionObject));
      }
    },
    async setUserData(user: AuthorizedUser): Promise<void> {
      if (this.userId !== InitialState) return;

      try {
        this.setUser(user);

        const localStorageItem = localStorage.getItem(`mfaSkipped_${user.userId}`);
        const sessionObject = JSON.parse(localStorageItem || '{}');

        this.setMfaSkipped(isExpirationDateValid(sessionObject?.expiresAt));

        setUser({
          id: user.userId,
          logonId: user.logonId,
          groups: user.groups,
        });
      } catch (e) {
        captureError(e);
      }
    },
    async setUserDetails(userId: string): Promise<void> {
      const userDetails = await fetchUserDetails(userId);
      if (userDetails) {
        this.userDetails = userDetails;
      }
    },
  },
});
